import { MCPConfiguration, ReportColumn } from "@/types/evaluate";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  MCPFunction,
  useListMCPFunctions,
} from "@/queries/evals/useListMCPFunctions";
import { useEffect, useState } from "react";
import { formatInputVariable } from "@/utils/evaluate";
import { useBandaid } from "@/components/Evaluate/TableComponents/utils/BandaidContext";
import { ModalStep } from "@/components/Evaluate/TableComponents/NewColumnModal/ModalRouter";
import { PencilIcon } from "@heroicons/react/outline";
import { UseQueryResult } from "@tanstack/react-query";
import LoadingSpinner from "@/components/LoadingSpinner";
import { CheckIcon } from "@radix-ui/react-icons";
import SourceName from "@/components/Evaluate/TableComponents/NewColumnModal/SourceName";

const FunctionDropDown = ({
  selected,
  setSelectedFunction,
  result,
}: {
  selected: string | null;
  setSelectedFunction: (McpFunction: MCPFunction) => void;
  result: UseQueryResult<MCPFunction[], unknown>;
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="w-full" disabled={!result.isSuccess}>
        <div className="flex items-center justify-between">
          {result.isFetching && <LoadingSpinner size={5} />}
          {selected ? (
            <span>{selected}</span>
          ) : (
            <span className="text-gray-500">Select a Function...</span>
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {result.isSuccess &&
          result.data.map((mcpFunction) => (
            <DropdownMenuItem
              key={mcpFunction.name}
              onSelect={() => setSelectedFunction(mcpFunction)}
            >
              {mcpFunction.name}
              {selected === mcpFunction.name && (
                <CheckIcon className="ml-auto h-4 w-4 text-primary" />
              )}
            </DropdownMenuItem>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const DataInput = ({
  schema,
  availableColumns,
  inputData,
  setInputData,
  editable,
}: {
  schema: Record<string, any>;
  availableColumns: Array<ReportColumn>;
  inputData: Record<string, string>;
  setInputData: (
    data: (prev: Record<string, string>) => Record<string, string>,
  ) => void;
  editable: boolean;
}) => {
  const bandaid = useBandaid();

  return (
    <div className="col-span-2 rounded-md border border-gray-200 bg-gray-50 p-3">
      {Object.keys(schema.properties).map((key) => (
        <div className="mt-3 grid grid-cols-3 first:mt-0">
          <label
            htmlFor="json-path"
            className="col-span-1 mr-2 mt-4 whitespace-nowrap break-all text-right font-mono text-sm text-gray-600"
          >
            {key}
          </label>
          <div className="col-span-2 mt-1">
            <DropdownMenu>
              <DropdownMenuTrigger
                disabled={!editable}
                className="col-span-2 w-full"
              >
                {inputData[key] || (
                  <span className="font-normal text-gray-500">
                    Select a {bandaid ? "node" : "column"}...
                  </span>
                )}
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem
                  key="blank"
                  onSelect={() =>
                    setInputData((prev) => {
                      let newData = { ...prev };
                      delete newData[key];
                      return { ...newData };
                    })
                  }
                >
                  <span className="font-normal text-gray-500">None</span>
                  {inputData[key] === undefined && (
                    <CheckIcon className="ml-auto h-4 w-4 text-primary" />
                  )}
                </DropdownMenuItem>
                {availableColumns.map((column) => (
                  <DropdownMenuItem
                    key={column.name}
                    onSelect={() =>
                      setInputData((prev) => {
                        let newData: Record<string, string> = {};
                        newData[key] = column.name;
                        return { ...prev, ...newData };
                      })
                    }
                  >
                    {formatInputVariable(column.column_type, column.name)}
                    {inputData[key] === column.name && (
                      <CheckIcon className="ml-auto h-4 w-4 text-primary" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      ))}
    </div>
  );
};

const HomeStep = ({
  navigateAway,
  availableColumns,
  setCurrentNavigation,
  configuration,
  columnName,
  setColumnName,
  setConfiguration,
  editable,
}: {
  navigateAway: () => void;
  availableColumns: Array<ReportColumn>;
  setCurrentNavigation: (navigation: string) => void;
  configuration: Partial<MCPConfiguration>;
  columnName: string;
  setColumnName: (name: string) => void;
  setConfiguration: (
    data:
      | Partial<MCPConfiguration>
      | ((prevState: Partial<MCPConfiguration>) => Partial<MCPConfiguration>),
  ) => void;
  editable: boolean;
}) => {
  const bandaid = useBandaid();

  const [mcpSchema, setMcpSchema] = useState<Record<string, object> | null>();
  const [functionName, setFunctionName] = useState<string | null>(
    configuration.function_name || null,
  );

  const [inputData, setInputData] = useState<Record<string, string>>(() => {
    let inputs: Record<string, string> = {};
    if (configuration.input_data !== undefined) {
      configuration.input_data.forEach((value) => {
        inputs[value.path] = value.source;
      });
    }
    return inputs;
  });

  const updateInputData = (
    func: (prev: Record<string, string>) => Record<string, string>,
  ) => {
    setInputData((prev: Record<string, string>) => {
      let updateInputData = func(prev);
      setConfiguration((config) => ({
        ...config,
        ...{
          input_data: Object.keys(updateInputData).map((key) => ({
            path: key,
            source: updateInputData[key],
          })),
        },
      }));
      return updateInputData;
    });
  };

  const result = useListMCPFunctions(
    configuration.server_configuration?.server_url,
    configuration.server_configuration?.headers,
  );

  const selectFunction = (McpFunction: MCPFunction) => {
    setFunctionName(McpFunction.name);
    setConfiguration((config) => {
      return { ...config, ...{ function_name: McpFunction.name } };
    });
  };

  useEffect(() => {
    if (result.data) {
      result.data.forEach((func) => {
        if (func.name === functionName) {
          setMcpSchema(func.input_schema);
        }
      });
    }
  }, [result.data, configuration.function_name, functionName]);

  const saveAction = () => {
    navigateAway();
  };

  return (
    <ModalStep
      navigateNext={editable ? saveAction : undefined}
      nextButtonText={editable ? "Save" : undefined}
      navigatePrevious={editable ? undefined : saveAction}
    >
      <div className="grid grid-cols-[auto,1fr] gap-4">
        <h2 className="col-span-2 text-lg font-semibold">
          Configure MCP action.
        </h2>
        <SourceName
          name={columnName!}
          columnCount={availableColumns.length}
          isValid={true}
          setName={setColumnName}
          isNode={bandaid}
          readonly={!editable}
          variant="PROMPT_TEMPLATE"
        />
        <div>Connect to Server:</div>
        <div className="flex items-center space-x-2">
          {configuration.server_configuration?.server_url ? (
            <>
              <span className="flex-1 break-all text-gray-600">
                {result.isFetching
                  ? "Connecting..."
                  : result.isError
                  ? "Unreachable."
                  : "Connected."}
              </span>
              <Button
                size="tinyIcon"
                variant="outline"
                onClick={() => setCurrentNavigation("server-connect")}
              >
                <PencilIcon className="inline-block h-4 w-4 text-gray-600" />
              </Button>
            </>
          ) : (
            <Button
              onClick={() => {
                setCurrentNavigation("server-connect");
              }}
              className="w-full bg-blue-500 px-4 py-2 text-center text-white"
            >
              Configure
            </Button>
          )}
        </div>

        <div className="col-span-2">
          <FunctionDropDown
            selected={functionName}
            setSelectedFunction={selectFunction}
            result={result}
          />
        </div>

        {mcpSchema === null || mcpSchema === undefined ? (
          <></>
        ) : (
          <>
            <label htmlFor="json-path" className="col-span-2">
              Input data:
            </label>
            <DataInput
              schema={mcpSchema}
              availableColumns={availableColumns}
              editable={editable}
              inputData={inputData}
              setInputData={updateInputData}
            />
          </>
        )}
      </div>
    </ModalStep>
  );
};

export default HomeStep;
